import { useMemo } from "react";
import { shortAddr } from "../../wallets/lib/format";
import { ApiDown, Empty, RebuildNote, GridNote, Panel, SearchBox, SourceError } from "../components/Bits";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { FLAGS_REFRESH_MS, useFlaggedFeed } from "../lib/data";
import { Updated } from "../lib/freshness";
import { count, usd } from "../lib/format";
import { buildFlow, type FlowMarket } from "../lib/flagged";
import { matches } from "../lib/markets";
import { openMarket, useSearchParam } from "../lib/nav";
import { useApiStatus } from "../lib/status";

const MAX_MARKETS = 60;

function FlowRow({ m, scale }: { m: FlowMarket; scale: number }) {
	return (
		<button
			type="button"
			onClick={() => openMarket(m.conditionId)}
			className="group block w-full border-b border-line/50 px-2 py-1.5 text-left hover:bg-raised"
		>
			<div className="flex items-baseline justify-between gap-3 text-[11px]">
				<span className="min-w-0 truncate text-bright group-hover:text-up" title={m.question}>{m.question}</span>
				<span className="num shrink-0">
					<span className="text-mute">{m.wallets}w </span>
					<span className="font-bold text-bright">{usd(m.total)}</span>
				</span>
			</div>
			<div className="mt-1 flex h-3.5 gap-px" style={{ width: `${Math.max(4, (m.total / scale) * 100)}%` }}>
				{m.blocks.map((b) => (
					<span
						key={`${b.wallet}:${b.side}`}
						title={`${shortAddr(b.wallet)} · ${b.outcome} · ${usd(b.usd)}`}
						className={b.side === "first" ? "bg-up/80" : "bg-dn/80"}
						style={{ flexGrow: b.usd, flexBasis: 0, minWidth: 2 }}
					/>
				))}
			</div>
		</button>
	);
}

export function FlaggedFlowView() {
	const api = useApiStatus();
	const feed = useFlaggedFeed();
	const [q, setQ] = useSearchParam();
	const flow = useMemo(() => buildFlow(feed.rows), [feed.rows]);
	const shown = useMemo(() => flow.filter((m) => matches(q, m.question)).slice(0, MAX_MARKETS), [flow, q]);
	const scale = shown[0]?.total ?? 1;
	const down = api.state === "down" && feed.rows.length === 0;

	return (
		<View>
			<ViewHeader
				title="flagged flow by market"
				meta={flow.length ? `${count(flow.length)} markets` : undefined}
				desc="Capital in flagged positions per market. One block per wallet and side, sized by dollars. A flag is a pattern, not proof."
			>
				<SearchBox value={q} onChange={setQ} placeholder="search markets" />
			</ViewHeader>
			<RebuildNote />
			<div className="flex shrink-0 items-center gap-4 text-[10px] text-dim">
				<span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-up/80" />Yes / first outcome</span>
				<span className="flex items-center gap-1.5"><span className="h-2 w-2 bg-dn/80" />No / other outcome</span>
			</div>
			<Panel>
				<div className="min-h-0 flex-1 overflow-y-auto bg-panel">
					{down ? (
						<ApiDown what="flagged flow" />
					) : feed.error && feed.rows.length === 0 ? (
						<SourceError source="api.polygains.com" onRetry={api.recheck} />
					) : feed.isLoading || api.state === "checking" ? (
						<Empty>loading flagged positions…</Empty>
					) : shown.length === 0 ? (
						<Empty>No flagged flow matches.</Empty>
					) : (
						shown.map((m) => <FlowRow key={m.conditionId} m={m} scale={scale} />)
					)}
				</div>
				<GridNote className="min-h-[75px] sm:min-h-0">
					<Updated at={feed.updatedAt} refreshMs={FLAGS_REFRESH_MS} failing={Boolean(feed.error) || api.state === "down"} />
					{feed.rows.length ? `built from the ${count(feed.rows.length)} most recent of ${count(feed.total)} flagged positions, not all history · top ${MAX_MARKETS} markets · ` : ""}source: PolyGains /alerts
					{feed.canLoadMore ? (
						<button type="button" onClick={feed.loadMore} disabled={feed.loadingMore} className="ml-2 border border-line px-1.5 text-fg hover:border-up/50 hover:text-up">
							{feed.loadingMore ? "loading" : "load 200 more"}
						</button>
					) : null}
				</GridNote>
			</Panel>
		</View>
	);
}
